import { LICENSE_TOKEN_KEY } from "@coach/license-client";
import { api, apiFetch } from "./api.js";
import { getOrCreateDeviceId } from "./device-id.js";
import { invalidateActiveMe } from "./use-active-org.js";

/**
 * "Active selection" is a triple of (server activeOrganizationId,
 * server activeTeamId, locally cached license JWT). The two server-side
 * pieces flip via dedicated endpoints; the license JWT carries the orgId
 * as a signed claim and must be re-issued whenever the active org
 * changes, otherwise the device keeps talking to the old tenant.
 *
 * The helpers below are the *only* sanctioned way to flip the active org
 * or team from the app — they encapsulate license re-issue, /me cache
 * invalidation and the localStorage cleanup needed before the post-reload
 * boot can pick up the new state without races.
 */

const ME_STORAGE_KEY = "coach.me.v1";
const CONFIRMED_ORG_KEY = "coach.sel.confirmedOrgId";
const CONFIRMED_TEAM_KEY = "coach.sel.confirmedTeamId";
const SEEN_USER_KEY = "coach.sel.lastUserId";

/**
 * Switch the signed-in coach's active organisation. Posts to
 * `/api/v1/org/active` (validates membership server-side, persists
 * activeOrganizationId, resets activeTeamId), then re-issues a license
 * JWT bound to the new org, wipes the local /me cache and finally hard
 * reloads so the sync engine + license-bound state can rebuild from a
 * clean slate.
 *
 * Re-issuing the license is essential: the sync provider boots against
 * `claims.orgId`, so without a fresh JWT a switch would silently keep
 * pulling the previous tenant's data.
 */
export async function switchActiveOrganization(orgId: string): Promise<void> {
  await apiFetch<{ ok: boolean }>("/api/v1/org/active", {
    method: "POST",
    body: JSON.stringify({ organizationId: orgId }),
  });

  // Mint a fresh license against the new org so claims.orgId is correct
  // on the next boot. We deliberately issue *before* touching the cached
  // token so a failure (network drop, license rejected by billing,
  // forbidden, …) leaves the previous token in place and the app stays
  // usable instead of dropping into the AuthGate "no license" screen.
  const deviceId = getOrCreateDeviceId();
  const issued = await api().license.issue({
    orgId,
    ...(deviceId ? { deviceId } : {}),
  });
  if (!issued.ok) {
    throw new Error(`license_issue_failed:${issued.reason}`);
  }
  try {
    localStorage.setItem(LICENSE_TOKEN_KEY, issued.token);
  } catch {
    /* ignore */
  }

  markConfirmedOrg(orgId);
  // The team for the new org is unknown — clear any prior confirmation
  // so the gate prompts /select/team if multiple are available.
  clearConfirmedTeam();
  invalidateActiveMe();
  try {
    localStorage.removeItem(ME_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.location.replace("/");
}

/**
 * Switch the active team within the current org. No license re-issue is
 * needed — the JWT only encodes org-level identity, and the sync engine
 * filters by team client-side off `useActiveTeamId()`.
 */
export async function switchActiveTeam(teamId: string): Promise<void> {
  await apiFetch<{ ok: boolean }>("/api/v1/teams/active", {
    method: "POST",
    body: JSON.stringify({ teamId }),
  });
  markConfirmedTeam(teamId);
  invalidateActiveMe();
  try {
    localStorage.removeItem(ME_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.location.replace("/");
}

// ── Local "the user has explicitly confirmed this choice" markers ──────
// Drives the /select/org and /select/team gate: a fresh login (different
// userId than last seen) or an unconfirmed active org/team triggers the
// selection screens; after the user picks, we stamp the choice so future
// boots go straight to the cockpit.

export function markConfirmedOrg(orgId: string): void {
  try {
    localStorage.setItem(CONFIRMED_ORG_KEY, orgId);
  } catch {
    /* ignore */
  }
}

export function markConfirmedTeam(teamId: string): void {
  try {
    localStorage.setItem(CONFIRMED_TEAM_KEY, teamId);
  } catch {
    /* ignore */
  }
}

export function clearConfirmedTeam(): void {
  try {
    localStorage.removeItem(CONFIRMED_TEAM_KEY);
  } catch {
    /* ignore */
  }
}

export function getConfirmedOrg(): string | null {
  try {
    return localStorage.getItem(CONFIRMED_ORG_KEY);
  } catch {
    return null;
  }
}

export function getConfirmedTeam(): string | null {
  try {
    return localStorage.getItem(CONFIRMED_TEAM_KEY);
  } catch {
    return null;
  }
}

/**
 * Detects a fresh login by comparing the supplied user id against the
 * last one we saw. On change we drop both selection markers so the gate
 * always re-prompts after switching accounts.
 */
export function noteUserAndResetIfChanged(userId: string | null): void {
  if (!userId) return;
  let prev: string | null = null;
  try {
    prev = localStorage.getItem(SEEN_USER_KEY);
  } catch {
    /* ignore */
  }
  if (prev === userId) return;
  try {
    localStorage.setItem(SEEN_USER_KEY, userId);
    localStorage.removeItem(CONFIRMED_ORG_KEY);
    localStorage.removeItem(CONFIRMED_TEAM_KEY);
  } catch {
    /* ignore */
  }
}
